import { useEffect, useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { CreateWeightForm } from 'src/@types/validations/Animal'
import { useGetAnimalById, useGetProductiveAnimalList, useUpdateAnimal } from 'src/hooks/api/animal'
import { toast } from 'react-toastify'
import { toastSyncSuccess } from 'src/utils/syncToast'
import { parseNumber } from 'src/utils/weighting/calculateWeighting'
import { AnimalHistoricParams } from 'src/domain/animal/AnimalHistoric'
import { useAnimalWeighting } from 'src/pages/Protected/Animal/hooks/useAnimalWeighting'

export interface WeighingFormData
  extends Omit<AnimalHistoricParams, 'peso' | 'fitaComprimento' | 'fitaPerimetro' | 'perimetro'> {
  tipoPesagem: 'pesagem' | 'pesagem_fita'
  fitaComprimento: string
  fitaPerimetro: string
  perimetro: string
  peso: string
}

export const useWeighing = () => {
  const [isSaving, setIsSaving] = useState(false)
  const { watch, setValue, clearErrors, reset } = useFormContext<WeighingFormData>()

  const selectedAnimalId = watch('idAnimal')

  const { data: productiveAnimalHistoric = [] } = useGetProductiveAnimalList(selectedAnimalId!)

  const { data: animalDetails } = useGetAnimalById(selectedAnimalId!)

  const { calculateAndSaveWeighting } = useAnimalWeighting(productiveAnimalHistoric)

  const { mutateAsync: updateAnimalMutation } = useUpdateAnimal()

  const perimeter = parseNumber(watch('fitaPerimetro'))
  const width = parseNumber(watch('fitaComprimento'))

  const weighingType = watch('tipoPesagem')

  const calculateApproximateWeight = useCallback(() => {
    if (!perimeter || !width) return

    const peso = Math.pow(width / 100, 2) * (perimeter / 100) * 87.5

    setValue('peso', peso.toFixed(2))
  }, [perimeter, width, setValue])

  useEffect(() => {
    clearErrors()
  }, [weighingType])

  useEffect(() => {
    if (weighingType === 'pesagem_fita') {
      calculateApproximateWeight()
    }
  }, [weighingType, perimeter, width, calculateApproximateWeight])

  const onSubmit = async (formData: CreateWeightForm) => {
    if (!animalDetails) return

    setIsSaving(true)

    const payload = {
      ...animalDetails,
      pesoAtual: parseNumber(formData.peso),
      ...(formData.tipoPesagem === 'pesagem'
        ? {
            perimetro: parseNumber(formData.perimetro)
          }
        : {
            fitaComprimento: parseNumber(formData.fitaComprimento),
            fitaPerimetro: parseNumber(formData.fitaPerimetro)
          })
    }
    try {
      await calculateAndSaveWeighting(payload as any)
      await updateAnimalMutation(payload)

      reset()
      toastSyncSuccess('Pesagem salva com sucesso')
    } catch (error) {
      toast.error('Erro ao salvar pesagem')
    } finally {
      setIsSaving(false)
    }
  }

  return {
    isSaving,
    calculateApproximateWeight,
    onSubmit
  }
}
